import React from 'react';
import { motion } from 'framer-motion';
import { Play, Layers, UserCheck, Sparkles, Brain, Clock, Zap, Rocket, BarChart3 } from 'lucide-react';
export function SolutionSlide() {
  const differentiators = [{
    icon: Clock,
    text: 'Designed for real clinical attention spans'
  }, {
    icon: Zap,
    text: 'Active engagement, not passive content'
  }, {
    icon: Rocket,
    text: 'Faster to deploy, easier to scale'
  }, {
    icon: BarChart3,
    text: 'Insight emerges from meaningful interaction'
  }];
  return <div className="h-full flex flex-col" data-id="element-438">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 h-full items-center" data-id="element-439">
        {/* Left: Value Proposition */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="col-span-1 space-y-5" data-id="element-440">
          {/* Core Insight */}
          <div data-id="element-441">
            <p className="text-sm font-medium text-purple-600 uppercase tracking-wider mb-2" data-id="element-442">
              The insight
            </p>
            <h3 className="text-xl font-bold text-gray-900 leading-snug" data-id="element-443">
              HCPs don't lack information — they lack{' '}
              <span className="text-purple-600" data-id="element-444">time</span>.
            </h3>
          </div>

          <p className="text-sm text-gray-500 leading-relaxed" data-id="element-445">
            Snakker turns complex science into short, visual, snackable
            engagement — engineered for how humans actually consume information
            — without sacrificing compliance or rigor.
          </p>

          {/* Differentiators */}
          <div className="space-y-2.5" data-id="element-446">
            {differentiators.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.3 + i * 0.1
          }} className="flex items-start gap-2.5" data-id="element-447">
                <div className="w-6 h-6 rounded-md bg-purple-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-448">
                  <item.icon className="w-3.5 h-3.5 text-purple-600" data-id="element-449" />
                </div>
                <span className="text-sm text-gray-700 leading-snug" data-id="element-450">
                  {item.text}
                </span>
              </motion.div>)}
          </div>

          {/* Snaks Format — compact */}
          <div className="p-4 rounded-xl bg-purple-50 border border-purple-200" data-id="element-451">
            <div className="flex items-center gap-2 mb-2" data-id="element-452">
              <Play className="w-4 h-4 text-purple-600 fill-purple-600" data-id="element-453" />
              <h4 className="text-sm font-bold text-gray-900" data-id="element-454">
                Snaks™ Format
              </h4>
              <span className="text-xs text-gray-400 ml-auto" data-id="element-455">
                20–40 sec episodes
              </span>
            </div>
            <div className="flex items-center justify-between text-xs font-mono text-purple-600 bg-white p-2 rounded-md border border-purple-100" data-id="element-456">
              <span data-id="element-457">Promise</span>
              <span className="text-gray-300" data-id="element-458">→</span>
              <span data-id="element-459">Action</span>
              <span className="text-gray-300" data-id="element-460">→</span>
              <span data-id="element-461">Payoff</span>
            </div>
          </div>

          {/* IQVIA badge */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200" data-id="element-462">
            <UserCheck className="w-4 h-4 text-green-600" data-id="element-463" />
            <span className="text-xs font-medium text-gray-600" data-id="element-464">
              IQVIA Snak-Key — compliant HCP gating built-in
            </span>
          </div>
        </motion.div>

        {/* Right: The Engine Visualization */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.2
      }} className="col-span-2 relative flex flex-col gap-6" data-id="element-465">
          {/* Closing the Loop Image */}
          <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white shadow-sm" data-id="element-466">
            <img src="https://cdn.magicpatterns.com/uploads/sggdKvieRcUeM5EAotaapX/Screenshot_2026-04-07_at_11.48.43.png" alt="Closing the loop: Compose, Cast, Report, and Iterate cycle" className="w-full h-auto object-contain" data-id="element-467" />
          </div>

          {/* Runtime Layers (compact) */}
          <div className="grid grid-cols-3 gap-3" data-id="element-468">
            <motion.div initial={{
            y: 20,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            delay: 0.4
          }} className="flex items-center gap-3 p-3 rounded-xl bg-purple-50 border border-purple-200 shadow-sm" data-id="element-469">
              <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0 border border-purple-100" data-id="element-470">
                <Brain className="w-5 h-5 text-purple-600" data-id="element-471" />
              </div>
              <div data-id="element-472">
                <h4 className="text-sm font-bold text-gray-900" data-id="element-473">Brainware</h4>
                <p className="text-purple-600 text-xs" data-id="element-474">Story & Strategy</p>
              </div>
            </motion.div>

            <motion.div initial={{
            y: 20,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            delay: 0.5
          }} className="flex items-center gap-3 p-3 rounded-xl bg-indigo-50 border border-indigo-200 shadow-sm" data-id="element-475">
              <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0 border border-indigo-100" data-id="element-476">
                <Sparkles className="w-5 h-5 text-indigo-600" data-id="element-477" />
              </div>
              <div data-id="element-478">
                <h4 className="text-sm font-bold text-gray-900" data-id="element-479">Createware</h4>
                <p className="text-indigo-600 text-xs" data-id="element-480">Production</p>
              </div>
            </motion.div>

            <motion.div initial={{
            y: 20,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            delay: 0.6
          }} className="flex items-center gap-3 p-3 rounded-xl bg-blue-50 border border-blue-200 shadow-sm" data-id="element-481">
              <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0 border border-blue-100" data-id="element-482">
                <Layers className="w-5 h-5 text-blue-600" data-id="element-483" />
              </div>
              <div data-id="element-484">
                <h4 className="text-sm font-bold text-gray-900" data-id="element-485">Software</h4>
                <p className="text-blue-600 text-xs" data-id="element-486">Loops & Optimization</p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>;
}